import React, { useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  Dashboard as DashboardIcon,
  GroupAdd as GroupAddIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import confetti from 'canvas-confetti';
import SetupLayout from './SetupLayout';

const SetupComplete: React.FC = () => {
  const navigate = useNavigate();
  const signupData = JSON.parse(sessionStorage.getItem('signupData') || '{}');
  const selectedPlan = signupData.selectedPlan || { name: 'Free' };

  useEffect(() => {
    // Trigger confetti
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#dc2626', '#22c55e', '#3b82f6', '#f59e0b'],
    });
  }, []);

  const adminUrl = `https://cruvzmail.com/admin/`;
  const webmailUrl = `https://cruvzmail.com/login`;

  const quickActions = [
    { icon: <EmailIcon />, title: 'Login to Webmail', desc: 'Access your inbox', path: webmailUrl, color: '#3b82f6', external: true },
    { icon: <GroupAddIcon />, title: 'Add Users', desc: 'Invite team members', path: '/setup/add-users', color: '#22c55e', external: false },
    { icon: <SettingsIcon />, title: 'Settings', desc: 'Configure your account', path: '/settings', color: '#f59e0b', external: false },
    { icon: <DashboardIcon />, title: 'Admin Panel', desc: 'Manage your domain', path: adminUrl, color: '#8b5cf6', external: true },
  ];

  return (
    <SetupLayout activeStep={7} backPath="/setup/dns" hideBack>
      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, px: 3 }}>
        <Box sx={{ textAlign: 'center', maxWidth: 600, mb: 6 }}>
          <Box sx={{ width: 100, height: 100, borderRadius: '50%', bgcolor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 60, color: '#22c55e' }} />
          </Box>

          <Typography variant="h3" sx={{ fontWeight: 700, color: '#111827', mb: 2, fontFamily: 'Inter, sans-serif' }}>
            You're all set! 🎉
          </Typography>
          <Typography sx={{ color: '#6b7280', fontSize: '1.125rem', mb: 4, fontFamily: 'Inter, sans-serif' }}>
            Your email account is ready to use. Start exploring all the features available to you.
          </Typography>

          {/* Account Summary */}
          <Box sx={{ bgcolor: '#f8fafc', borderRadius: 3, p: 4, mb: 4, border: '1px solid #e5e7eb', textAlign: 'left' }}>
            <Typography sx={{ fontWeight: 600, color: '#111827', mb: 3, fontFamily: 'Inter, sans-serif' }}>
              Account Summary
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography sx={{ color: '#6b7280', fontSize: '0.75rem', fontFamily: 'Inter, sans-serif' }}>Organization</Typography>
                <Typography sx={{ fontWeight: 600, color: '#111827', fontFamily: 'Inter, sans-serif' }}>{signupData.organization || 'Your Organization'}</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: '#6b7280', fontSize: '0.75rem', fontFamily: 'Inter, sans-serif' }}>Plan</Typography>
                <Typography sx={{ fontWeight: 600, color: '#111827', fontFamily: 'Inter, sans-serif' }}>{selectedPlan.name}</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: '#6b7280', fontSize: '0.75rem', fontFamily: 'Inter, sans-serif' }}>Domain</Typography>
                <Typography sx={{ fontWeight: 600, color: '#111827', fontFamily: 'Inter, sans-serif' }}>{signupData.domain || 'yourdomain.com'}</Typography>
              </Box>
              <Box>
                <Typography sx={{ color: '#6b7280', fontSize: '0.75rem', fontFamily: 'Inter, sans-serif' }}>Admin Email</Typography>
                <Typography sx={{ fontWeight: 600, color: '#111827', fontFamily: 'Inter, sans-serif' }}>{signupData.adminEmail || 'admin@yourdomain.com'}</Typography>
              </Box>
            </Box>
          </Box>

          {/* Quick Actions */}
          <Typography sx={{ fontWeight: 600, color: '#111827', mb: 3, fontFamily: 'Inter, sans-serif' }}>
            Quick Actions
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 4 }}>
            {quickActions.map((action, i) => (
              <Box
                key={i}
                onClick={() => action.external ? window.open(action.path, '_blank') : navigate(action.path)}
                sx={{
                  p: 3,
                  bgcolor: '#f8fafc',
                  borderRadius: 2,
                  border: '1px solid #e5e7eb',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: action.color, transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
                }}
              >
                <Box sx={{ color: action.color, mb: 1 }}>{action.icon}</Box>
                <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: '0.875rem', fontFamily: 'Inter, sans-serif' }}>
                  {action.title}
                </Typography>
                <Typography sx={{ color: '#6b7280', fontSize: '0.75rem', fontFamily: 'Inter, sans-serif' }}>
                  {action.desc}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography sx={{ color: '#6b7280', fontSize: '0.875rem', fontFamily: 'Inter, sans-serif', mb: 1 }}>
              Your admin panel URL:
            </Typography>
            <Typography 
              component="a" 
              href={adminUrl} 
              target="_blank"
              sx={{ 
                color: '#dc2626', 
                fontWeight: 600, 
                fontSize: '1rem', 
                fontFamily: 'Inter, sans-serif',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              {adminUrl}
            </Typography>
          </Box>

          <Button
            variant="contained"
            onClick={() => window.open(adminUrl, '_blank')}
            sx={{ px: 6, py: 1.5, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' }, fontWeight: 600, fontSize: '1rem', textTransform: 'none', borderRadius: 2 }}
          >
            Login to Admin Panel
          </Button>
        </Box>
      </Box>
    </SetupLayout>
  );
};

export default SetupComplete;
